import { useEffect, useState } from "react";
import api, { apiError, inr } from "../../lib/api";
import { PageHeader, Card, Loading, Empty } from "../../components/admin/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Trash2, RotateCcw, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Bin() {
  const [rows, setRows] = useState(null);
  const [schools, setSchools] = useState([]);
  const [pagesBin, setPagesBin] = useState([]);
  const [regsBin, setRegsBin] = useState([]);
  const [target, setTarget] = useState(null);
  const [schoolTarget, setSchoolTarget] = useState(null);
  const [pageTarget, setPageTarget] = useState(null);
  const [regTarget, setRegTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setRows(null);
    api.get("/payments/bin").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/schools/bin").then((r) => setSchools(r.data)).catch(() => setSchools([]));
    api.get("/pages/bin").then((r) => setPagesBin(r.data)).catch(() => setPagesBin([]));
    api.get("/registrations/bin").then((r) => setRegsBin(r.data)).catch(() => setRegsBin([]));
  };
  useEffect(() => { load(); }, []);

  const restoreReg = async () => {
    setBusy(true);
    try {
      await api.post(`/registrations/${regTarget.id}/restore`);
      toast.success("Registration restored");
      setRegTarget(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const restore = async () => {
    setBusy(true);
    try {
      await api.post(`/payments/${target.payment_id}/restore`);
      toast.success("Payment restored");
      setTarget(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const restoreSchool = async () => {
    setBusy(true);
    try {
      await api.post(`/schools/${schoolTarget.id}/restore`);
      toast.success("School restored");
      setSchoolTarget(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const restorePage = async () => {
    setBusy(true);
    try {
      await api.post(`/pages/${pageTarget.id}/restore`);
      toast.success("Payment page restored");
      setPageTarget(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div>
      <PageHeader title="Bin" subtitle="Deleted payment pages, payments and schools are kept here for 10 days, then permanently removed. Binned items do not count toward collection or revenue." />

      {pagesBin.length > 0 && (
        <Card className="mb-6">
          <div className="px-5 pt-4 text-sm font-semibold text-slate-700">Deleted Payment Pages</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="page-bin-table">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                {["Page", "School", "Regs", "Txns", "Collected", "Deleted", "Permanent", "Days Left", "Deleted By", ""].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {pagesBin.map((p) => (
                  <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid="page-bin-row">
                    <td className="px-5 py-3 font-medium">{p.title}<div className="text-xs text-slate-400 font-mono">/r/{p.short_code}</div></td>
                    <td className="px-5 py-3 text-slate-600">{p.school_name || "—"}</td>
                    <td className="px-5 py-3">{p.binned_registrations}</td>
                    <td className="px-5 py-3">{p.binned_payments}</td>
                    <td className="px-5 py-3 font-medium">{inr(p.collected ?? 0)}</td>
                    <td className="px-5 py-3 text-slate-500">{p.deleted_date}</td>
                    <td className="px-5 py-3 text-slate-500">{p.permanent_date}</td>
                    <td className="px-5 py-3"><span className="inline-flex items-center gap-1 text-amber-600 font-medium"><Clock className="w-3.5 h-3.5" />{p.days_remaining}d</span></td>
                    <td className="px-5 py-3 text-xs text-slate-400">{p.deleted_by || "—"}</td>
                    <td className="px-5 py-3 text-right"><button onClick={() => setPageTarget(p)} data-testid="page-restore-btn" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"><RotateCcw className="w-4 h-4" /> Restore</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {regsBin.length > 0 && (
        <Card className="mb-6">
          <div className="px-5 pt-4 text-sm font-semibold text-slate-700">Deleted Registrations</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="reg-bin-table">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                {["Registration", "Student", "Amount", "Payments", "Deleted", "Permanent", "Days Left", "Deleted By", ""].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {regsBin.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid="reg-bin-row">
                    <td className="px-5 py-3 font-mono text-xs">{r.registration_id}</td>
                    <td className="px-5 py-3 font-medium">{r.student_name}<div className="text-xs text-slate-400">{r.parent_name || ""}</div></td>
                    <td className="px-5 py-3 font-medium">{inr(r.amount ?? 0)}</td>
                    <td className="px-5 py-3">{r.binned_payments}</td>
                    <td className="px-5 py-3 text-slate-500">{r.deleted_date}</td>
                    <td className="px-5 py-3 text-slate-500">{r.permanent_date}</td>
                    <td className="px-5 py-3"><span className="inline-flex items-center gap-1 text-amber-600 font-medium"><Clock className="w-3.5 h-3.5" />{r.days_remaining}d</span></td>
                    <td className="px-5 py-3 text-xs text-slate-400">{r.deleted_by || "—"}</td>
                    <td className="px-5 py-3 text-right"><button onClick={() => setRegTarget(r)} data-testid="reg-restore-btn" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"><RotateCcw className="w-4 h-4" /> Restore</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {schools.length > 0 && (
        <Card className="mb-6">
          <div className="px-5 pt-4 text-sm font-semibold text-slate-700">Deleted Schools</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="school-bin-table">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                {["School", "Registrations", "Deleted", "Permanent", "Days Left", "Deleted By", ""].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {schools.map((s) => (
                  <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid="school-bin-row">
                    <td className="px-5 py-3 font-medium">{s.name}</td>
                    <td className="px-5 py-3">{s.binned_registrations}</td>
                    <td className="px-5 py-3 text-slate-500">{s.deleted_date}</td>
                    <td className="px-5 py-3 text-slate-500">{s.permanent_date}</td>
                    <td className="px-5 py-3"><span className="inline-flex items-center gap-1 text-amber-600 font-medium"><Clock className="w-3.5 h-3.5" />{s.days_remaining}d</span></td>
                    <td className="px-5 py-3 text-xs text-slate-400">{s.deleted_by || "—"}</td>
                    <td className="px-5 py-3 text-right"><button onClick={() => setSchoolTarget(s)} data-testid="school-restore-btn" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"><RotateCcw className="w-4 h-4" /> Restore</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card>
        {rows === null ? <Loading /> : (rows.length === 0 && regsBin.length === 0 && schools.length === 0 && pagesBin.length === 0) ? <Empty label="The Bin is empty." /> : rows.length === 0 ? null : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="bin-table">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                {["Payment ID", "Student", "Amount", "Original Status", "Deleted", "Permanent", "Days Left", "Deleted By", ""].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid="bin-row">
                    <td className="px-5 py-3 font-mono text-xs">{p.payment_id}</td>
                    <td className="px-5 py-3">{p.student_name}<div className="text-xs text-slate-400 font-mono">{p.registration_id}</div></td>
                    <td className="px-5 py-3 font-medium">{inr(p.paid_amount ?? p.detected_amount ?? 0)}</td>
                    <td className="px-5 py-3 capitalize">{(p.original_status || "").replace(/_/g, " ")}</td>
                    <td className="px-5 py-3 text-slate-500">{p.deleted_date}</td>
                    <td className="px-5 py-3 text-slate-500">{p.permanent_date}</td>
                    <td className="px-5 py-3"><span className="inline-flex items-center gap-1 text-amber-600 font-medium"><Clock className="w-3.5 h-3.5" />{p.days_remaining}d</span></td>
                    <td className="px-5 py-3 text-xs text-slate-400">{p.deleted_by || "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => setTarget(p)} data-testid="bin-restore-btn" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"><RotateCcw className="w-4 h-4" /> Restore</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent data-testid="restore-dialog">
          <DialogHeader><DialogTitle>Restore Payment?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Are you sure you want to restore this payment? Its amount will be included again in the relevant totals.</p>
          <DialogFooter>
            <button onClick={() => setTarget(null)} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 font-medium">Cancel</button>
            <button onClick={restore} disabled={busy} data-testid="confirm-restore-btn" className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Restore Payment</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!regTarget} onOpenChange={(o) => !o && setRegTarget(null)}>
        <DialogContent data-testid="restore-reg-dialog">
          <DialogHeader><DialogTitle>Restore Registration?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This will restore the registration and all of its data (student details and payments) back to the active system.</p>
          <DialogFooter>
            <button onClick={() => setRegTarget(null)} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 font-medium">Cancel</button>
            <button onClick={restoreReg} disabled={busy} data-testid="confirm-restore-reg-btn" className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Restore Registration</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={!!schoolTarget} onOpenChange={(o) => !o && setSchoolTarget(null)}>
        <DialogContent data-testid="restore-school-dialog">
          <DialogHeader><DialogTitle>Restore School?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This will restore the school and all of its students, registrations and payments to the active system.</p>
          <DialogFooter>
            <button onClick={() => setSchoolTarget(null)} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 font-medium">Cancel</button>
            <button onClick={restoreSchool} disabled={busy} data-testid="confirm-restore-school-btn" className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Restore School</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!pageTarget} onOpenChange={(o) => !o && setPageTarget(null)}>
        <DialogContent data-testid="restore-page-dialog">
          <DialogHeader><DialogTitle>Restore Payment Page?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Are you sure you want to restore this payment page and its associated data? Its payments will be included again in the main collection and revenue.</p>
          <DialogFooter>
            <button onClick={() => setPageTarget(null)} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 font-medium">Cancel</button>
            <button onClick={restorePage} disabled={busy} data-testid="confirm-restore-page-btn" className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Restore Page</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
